<template>
  <b-card
    no-body
    class="linked-assets-panel"
  >
    <div class="card-header d-flex flex-wrap align-items-center justify-content-between">
      <div>
        <h5 class="mb-25">
          {{ $t('Linked Assets') }}
          <b-badge
            v-if="totalItems"
            variant="light-primary"
            pill
            class="ml-50"
          >
            {{ totalItems }}
          </b-badge>
        </h5>
        <small class="text-muted">{{ $t('Link assets to follow this preventive maintenance schedule') }}</small>
      </div>
      <b-button
        size="sm"
        variant="primary"
        class="mt-50 mt-md-0"
        @click="openBindModal"
      >
        <feather-icon
          icon="PlusIcon"
          size="14"
          class="mr-25"
        />
        {{ $t('Bind Asset') }}
      </b-button>
    </div>

    <!-- Loading State -->
    <div
      v-if="isLoading"
      class="text-center py-3"
    >
      <b-spinner
        small
        variant="primary"
      />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="assets.length === 0"
      class="text-center py-3 empty-assets m-1"
    >
      <feather-icon
        icon="TruckIcon"
        size="36"
        class="text-muted mb-1"
      />
      <h6 class="mb-50 font-weight-bolder">
        {{ $t('No assets linked to this plan') }}
      </h6>
      <p class="text-muted small mb-1">
        {{ $t('Link assets to follow this preventive maintenance schedule') }}
      </p>
      <b-button
        size="sm"
        variant="primary"
        @click="openBindModal"
      >
        <feather-icon
          icon="PlusIcon"
          size="14"
          class="mr-25"
        />
        {{ $t('Bind Asset') }}
      </b-button>
    </div>

    <!-- Assets Table -->
    <div
      v-else
      class="table-responsive"
    >
      <table class="table table-hover mb-0">
        <thead class="thead-light">
          <tr>
            <th style="width: 50px;">
              #
            </th>
            <th>{{ $t('Asset') }}</th>
            <th>{{ $t('Manufacturer') }}</th>
            <th>{{ $t('Model') }}</th>
            <th
              class="text-right"
              style="width: 90px;"
            >
              {{ $t('Actions') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(asset, idx) in assets"
            :key="asset.id || asset.assetId || idx"
          >
            <td class="text-muted font-small-2 align-middle">
              {{ (currentPage - 1) * perPage + idx + 1 }}
            </td>
            <td class="align-middle">
              <div class="d-flex align-items-center">
                <b-avatar
                  size="40"
                  variant="light-primary"
                  :src="asset.iconUrl || null"
                  class="mr-75 flex-shrink-0"
                >
                  <feather-icon
                    v-if="!asset.iconUrl"
                    icon="TruckIcon"
                    size="18"
                  />
                </b-avatar>
                <div>
                  <span class="font-weight-bolder text-dark d-block">
                    {{ asset.plate || asset.chassis || '—' }}
                  </span>
                  <small
                    v-if="asset.plate && asset.chassis"
                    class="text-muted d-block"
                  >
                    Chassi: {{ asset.chassis }}
                  </small>
                </div>
              </div>
            </td>
            <td class="align-middle">
              <span class="font-weight-medium">
                {{ asset.manufacturerName || '—' }}
              </span>
            </td>
            <td class="align-middle">
              <b-badge
                v-if="asset.modelName"
                variant="light-primary"
              >
                {{ asset.modelName }}
              </b-badge>
              <span
                v-else
                class="text-muted"
              >—</span>
            </td>
            <td class="align-middle text-right">
              <b-button
                variant="flat-danger"
                size="sm"
                class="btn-icon"
                :title="$t('Remove')"
                @click="confirmDeleteBind(asset.id, asset.assetId)"
              >
                <feather-icon
                  icon="Trash2Icon"
                  size="16"
                />
              </b-button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination Footer -->
    <div
      v-if="assets.length > 0 && totalItems > perPage"
      class="card-footer d-flex justify-content-between align-items-center py-75"
    >
      <small class="text-muted">
        {{ $t('Showing') }} {{ (currentPage - 1) * perPage + 1 }} {{ $t('to') }}
        {{ Math.min(currentPage * perPage, totalItems) }} {{ $t('of') }} {{ totalItems }} {{ $t('entries') }}
      </small>
      <b-pagination
        v-model="currentPage"
        :total-rows="totalItems"
        :per-page="perPage"
        first-number
        last-number
        class="mb-0"
        prev-class="prev-item"
        next-class="next-item"
      >
        <template #prev-text>
          <feather-icon
            icon="ChevronLeftIcon"
            size="16"
          />
        </template>
        <template #next-text>
          <feather-icon
            icon="ChevronRightIcon"
            size="16"
          />
        </template>
      </b-pagination>
    </div>

    <!-- Modal: Vincular Ativo com VueAutosuggest -->
    <b-modal
      id="modal-bind-asset-to-plan"
      ref="bindModalRef"
      no-close-on-backdrop
      :title="$t('Bind Asset to Plan')"
      title-class="font-18 font-weight-bolder"
      hide-footer
      @hidden="resetBindModal"
    >
      <p class="text-muted small mb-1">
        {{ $t('Select vehicle') }}
      </p>

      <b-form-group
        :label="$t('Asset') + '*'"
        label-for="autosuggest-asset-input"
      >
        <vue-autosuggest
          v-model="searchAssetText"
          :suggestions="filteredOptions"
          :input-props="inputProps"
          @selected="selectHandler"
          @input="onInputChange"
        >
          <template slot-scope="{suggestion}">
            <div class="d-flex align-items-center py-25">
              <b-avatar
                size="28"
                variant="light-primary"
                :src="suggestion.item.iconUrl || null"
                class="mr-50 flex-shrink-0"
              >
                <feather-icon
                  v-if="!suggestion.item.iconUrl"
                  icon="TruckIcon"
                  size="14"
                />
              </b-avatar>
              <div>
                <span class="font-weight-bold d-block text-dark">
                  {{ suggestion.item.plate || suggestion.item.chassisNumber || suggestion.item.chassis || '—' }}
                </span>
                <small
                  v-if="suggestion.item.nickname"
                  class="text-muted"
                >
                  {{ suggestion.item.nickname }}
                </small>
                <small
                  v-if="suggestion.item.manufacturerName || suggestion.item.modelName"
                  class="text-muted ml-50"
                >
                  • {{ suggestion.item.manufacturerName }} {{ suggestion.item.modelName }}
                </small>
              </div>
            </div>
          </template>
        </vue-autosuggest>
      </b-form-group>

      <!-- Selection badge when chosen -->
      <div
        v-if="selectedAsset"
        class="alert alert-primary py-50 px-1 mt-1 mb-0 d-flex align-items-center justify-content-between"
      >
        <div class="d-flex align-items-center">
          <feather-icon
            icon="CheckCircleIcon"
            size="16"
            class="text-primary mr-50"
          />
          <span class="small font-weight-bold">
            {{ selectedAsset.plate || selectedAsset.chassisNumber || selectedAsset.chassis }}
            <span v-if="selectedAsset.nickname">({{ selectedAsset.nickname }})</span>
          </span>
        </div>
        <b-button
          variant="flat-danger"
          size="sm"
          class="btn-icon p-0"
          @click="clearSelectedAsset"
        >
          <feather-icon
            icon="XIcon"
            size="14"
          />
        </b-button>
      </div>

      <div class="d-flex justify-content-end align-items-center mt-2">
        <b-button
          variant="outline-secondary"
          class="mr-1"
          @click="closeBindModal"
        >
          {{ $t('Cancel') }}
        </b-button>
        <b-button
          variant="primary"
          :disabled="!selectedAsset || isBinding"
          @click="confirmBindAsset"
        >
          <b-spinner
            v-if="isBinding"
            small
            class="mr-50"
          />
          <feather-icon
            v-else
            icon="CheckIcon"
            size="14"
            class="mr-50"
          />
          {{ isBinding ? $t('Saving...') : $t('Add') }}
        </b-button>
      </div>
    </b-modal>
  </b-card>
</template>

<script>
import {
  computed,
  getCurrentInstance,
  onMounted,
  ref,
  watch,
} from '@vue/composition-api'
import {
  BAvatar,
  BBadge,
  BButton,
  BCard,
  BFormGroup,
  BModal,
  BPagination,
  BSpinner,
} from 'bootstrap-vue'
import { VueAutosuggest } from 'vue-autosuggest'
import store from '@/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const MODULE = 'app-management'

export default {
  name: 'PlanLinkedAssetsPanel',
  components: {
    BAvatar,
    BBadge,
    BButton,
    BCard,
    BFormGroup,
    BModal,
    BPagination,
    BSpinner,
    VueAutosuggest,
  },
  props: {
    planId: {
      type: [String, Number],
      required: true,
    },
  },
  setup(props, { emit }) {
    const instance = getCurrentInstance()
    const proxy = instance.proxy || instance

    const assets = ref([])
    const totalItems = ref(0)
    const currentPage = ref(1)
    const perPage = 10
    const isLoading = ref(false)

    const bindModalRef = ref(null)
    const isBinding = ref(false)
    const selectedAsset = ref(null)
    const searchAssetText = ref('')
    const filteredOptions = ref([])

    const inputProps = computed(() => ({
      id: 'autosuggest-asset-input',
      class: 'form-control',
      placeholder: proxy.$t('Search by plate, chassis or nickname...'),
      autocomplete: 'off',
    }))

    const fetchAssets = () => {
      if (!props.planId) return Promise.resolve()
      isLoading.value = true
      return store.dispatch(`${MODULE}/fetchAssetsByPlanId`, {
        planId: props.planId,
        page: currentPage.value,
        perPage,
      })
        .then(response => {
          assets.value = response.data?.data || []
          totalItems.value = response.data?.totalItems || 0
          emit('update:count', totalItems.value)
        })
        .catch(() => {
          assets.value = []
          totalItems.value = 0
          emit('update:count', 0)
        })
        .finally(() => {
          isLoading.value = false
        })
    }

    watch(() => props.planId, newId => {
      if (newId) fetchAssets()
    })

    watch(currentPage, () => {
      fetchAssets()
    })

    const confirmDeleteBind = (bindId, assetId) => {
      proxy.$swal({
        title: proxy.$t('Are you sure?'),
        text: proxy.$t("You can't revert your action"),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: proxy.$t('Yes'),
        cancelButtonText: proxy.$t('No'),
        customClass: {
          cancelButton: 'cancelBtn',
          confirmButton: 'mr-1',
        },
      }).then(result => {
        if (result.value) {
          store.dispatch(`${MODULE}/deleteBindPlan`, {
            assetId,
            preventiveMaintenancePlanId: props.planId,
            assetMaintenancePlanId: bindId,
          })
            .then(() => {
              proxy.$swal(proxy.$t('Deleted'), proxy.$t('You successfully deleted'), 'success')
              fetchAssets()
            })
            .catch(() => {
              proxy.$swal(proxy.$t('Error'), proxy.$t('An error occurred while trying to delete'), 'error')
            })
        }
      })
    }

    const onInputChange = text => {
      if (!text || text.length < 2) {
        filteredOptions.value = []
        return
      }
      store.dispatch(`${MODULE}/fetchAssets`, { q: text, perPage: 20 })
        .then(response => {
          const data = response.data?.data || []
          filteredOptions.value = [{ data }]
        })
        .catch(() => {
          filteredOptions.value = []
        })
    }

    const selectHandler = option => {
      if (!option || !option.item) return
      selectedAsset.value = option.item
      const plate = option.item.plate || option.item.chassisNumber || option.item.chassis || ''
      const nick = option.item.nickname ? ` - ${option.item.nickname}` : ''
      searchAssetText.value = `${plate}${nick}`
      filteredOptions.value = []
    }

    const clearSelectedAsset = () => {
      selectedAsset.value = null
      searchAssetText.value = ''
      filteredOptions.value = []
    }

    const openBindModal = () => {
      clearSelectedAsset()
      if (bindModalRef.value) {
        bindModalRef.value.show()
      }
    }

    const closeBindModal = () => {
      if (bindModalRef.value) {
        bindModalRef.value.hide()
      }
    }

    const resetBindModal = () => {
      clearSelectedAsset()
    }

    const confirmBindAsset = () => {
      if (!selectedAsset.value || isBinding.value) return

      const assetId = selectedAsset.value.id || selectedAsset.value.assetId || selectedAsset.value.value
      isBinding.value = true

      store.dispatch(`${MODULE}/bindMaintenancePlan`, {
        assetId,
        preventiveMaintenancePlanId: props.planId,
      })
        .then(() => {
          proxy.$toast({
            component: ToastificationContent,
            props: {
              title: proxy.$t('Asset added to maintenance plan'),
              icon: 'CheckCircleIcon',
              variant: 'success',
            },
          })
          closeBindModal()
          fetchAssets()
        })
        .catch(() => {
          proxy.$toast({
            component: ToastificationContent,
            props: {
              title: proxy.$t('An error occurred'),
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          isBinding.value = false
        })
    }

    onMounted(() => {
      fetchAssets()
    })

    return {
      assets,
      totalItems,
      currentPage,
      perPage,
      isLoading,
      bindModalRef,
      isBinding,
      selectedAsset,
      searchAssetText,
      filteredOptions,
      inputProps,
      confirmDeleteBind,
      openBindModal,
      closeBindModal,
      resetBindModal,
      onInputChange,
      selectHandler,
      clearSelectedAsset,
      confirmBindAsset,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-autosuggest.scss';

.autosuggest__results-container {
  position: relative;
  width: 100%;
}

.autosuggest__results {
  position: absolute;
  z-index: 1050;
  width: 100%;
  background-color: #fff;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 0.358rem;
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
  max-height: 250px;
  overflow-y: auto;
  padding: 0;
  margin: 4px 0 0 0;
  list-style: none;

  ul {
    list-style: none;
    padding-left: 0;
    margin-bottom: 0;

    li {
      padding: 0.6rem 1rem;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover,
      &.autosuggest__results-item--highlighted {
        background-color: #f8f8f8;
      }
    }
  }
}
</style>

<style lang="scss" scoped>
.empty-assets {
  background: #f7f7f9;
  border-radius: 0.428rem;
}
</style>
